import { theme } from '@atlas/shared/client/ui/core/theming';
import { css, unsafeCSS } from 'lit';
import { ColorLrColorBgButtonPrimaryDefault, FontFamilyLrFontFamilyDefault } from '@laredoute/design-system/tokens';

export const buttonBaseStyle = css`
    :host {
        display: block;
    }
    :host .button {
        --font-family: ${unsafeCSS(FontFamilyLrFontFamilyDefault)};
        --font-family: ${unsafeCSS(ColorLrColorBgButtonPrimaryDefault)};
        --font-size: var(--lr-text-14);
        --background-color: ${unsafeCSS(ColorLrColorBgButtonPrimaryDefault)};
        --color: var(--lr-color-primary);

        --padding: var(--lr-padding-medium);
        --border-color: var(--lr-border-primary-color);
        --border-radius: var(--lr-border-radius);
        font-family: var(--font-family);
        background-color: var(--background-color);
        color: var(--color);
        padding: var(--padding);
        font-size: var(--font-size);
        justify-content: center;
        display: inline-flex;
        align-items: center;
        text-align: center;
        cursor: pointer;
        overflow: hidden;
        transition: color 0.2s, background-color 0.2s, border-color 0.2s;
        z-index: 1;
        border: 0;
        text-decoration: none;
        vertical-align: middle;
        position: relative;
        font-weight: 600;
        border-radius: var(--border-radius);
        width: inherit;
        column-gap: 4px;

        &:not(.has-content) {
            column-gap: 0 !important;
        }

        &[disabled] {
            cursor: not-allowed;
        }
        &:not([disabled]):active {
            box-shadow: none !important;
        }

        &[disabled] {
            --background-color: var(--lr-background-primary-disabled);
            --color: var(--lr-color-primary-disabled);
        }

        .text {
            padding: 0 6px;
        }
    }

    @media (hover: hover) and (pointer: fine) {
        :host .button {
            &:not([disabled]):hover {
                --color: var(--lr-color-primary-hover);
                --background-color: var(--lr-background-primary-hover);
                &::before {
                    opacity: 1;
                }
            }
            &:not([disabled]):active {
                --background-color: var(--lr-background-primary-active);
                --color: var(--lr-color-primary-active);
                &::before {
                    opacity: 0;
                }
            }
        }
    }

    :host([size='small']) .button {
        --padding: var(--lr-padding-small);
        --font-size: var(--lr-text-12);
        .text {
            padding: 0 4px;
        }
    }
    :host([size='big']) .button {
        --padding: var(--lr-padding-big);
        --font-size: var(--lr-text-18);
        .text {
            padding: 0 8px;
        }
    }

    :host([fluid]) .button {
        width: 100%;
    }

    :host([iconPosition='left']) .button {
        flex-direction: row;
    }
    :host([iconPosition='right']) .button {
        flex-direction: row-reverse;
    }
`;

export const buttonStyle = css`
    :host([variant='primary']) .button::before {
        position: absolute;
        content: '';
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: -1;
        transition: opacity 0.2s ease-out;
        opacity: 0;
    }

    :host([reversed]) .button {
        --background-color: var(--lr-background-primary-reversed);
        --color: var(--lr-color-primary-reversed);
        &[disabled] {
            --background-color: var(--lr-background-primary-reversed-disabled);
            --color: var(--lr-color-primary-reversed-disabled);
        }
        &:not([disabled]):hover {
            --background-color: var(--lr-background-primary-reversed-hover);
            --color: var(--lr-color-primary-reversed-hover);
        }
        &:not([disabled]):active {
            --background-color: var(--lr-background-primary-reversed-active);
            --color: var(--lr-color-primary-reversed-active);
        }
    }

    :host([variant='secondary']) .button {
        --border-color: var(--lr-border-secondary-color);
        --background-color: var(--lr-background-secondary);
        --color: var(--lr-color-secondary);
        border: 2px solid var(--border-color);
        &[disabled] {
            --border-color: var(--lr-border-secondary-color-disabled);
            --background-color: var(--lr-background-secondary-disabled);
            --color: var(--lr-color-secondary-disabled);
        }
        &:not([disabled]):hover {
            --border-color: var(--lr-border-secondary-color-hover);
            --background-color: var(--lr-background-secondary-hover);
            --color: var(--lr-color-secondary-hover);
        }
        &:not([disabled]):active {
            --border-color: var(--lr-border-secondary-color-active);
            --background-color: var(--lr-background-secondary-active);
            --color: var(--lr-color-secondary-active);
        }
    }

    :host([variant='secondary'][reversed]) .button {
        --border-color: var(--lr-border-secondary-color-reversed);
        --background-color: var(--lr-background-secondary-reversed);
        --color: var(--lr-color-secondary-reversed);
        &[disabled] {
            --border-color: var(--lr-border-secondary-color-reversed-disabled);
            --background-color: var(--lr-background-secondary-reversed-disabled);
            --color: var(--lr-color-secondary-reversed-disabled);
        }
        &:not([disabled]):hover {
            --border-color: var(--lr-border-secondary-color-reversed-active);
            --background-color: var(--lr-background-secondary-reversed-active);
            --color: var(--lr-color-secondary-reversed-active);
        }
        &:not([disabled]):active {
            --border-color: var(--lr-border-secondary-color-reversed-active);
            --background-color: var(--lr-background-secondary-reversed-active);
            --color: var(--lr-color-secondary-reversed-active);
        }
    }

    :host([variant='tertiary']) .button,
    :host([variant='tertiary-transparent']) .button {
        --background-color: var(--lr-background-tertiary);
        --color: var(--lr-color-tertiary);
        &[disabled] {
            --background-color: var(--lr-background-tertiary-disabled);
            --color: var(--lr-color-tertiary-disabled);
        }
    }

    @media (hover: hover) and (pointer: fine) {
        :host([variant='tertiary']) .button,
        :host([variant='tertiary-transparent']) .button {
            &:not([disabled]):hover {
                --background-color: var(--lr-background-tertiary-hover);
                --color: var(--lr-color-tertiary-hover);
            }
            &:not([disabled]):active {
                --background-color: var(--lr-background-tertiary-active);
                --color: var(--lr-color-tertiary-active);
            }
        }
    }

    :host([variant='tertiary'][reversed]) .button,
    :host([variant='tertiary-transparent'][reversed]) .button {
        --background-color: var(--lr-background-tertiary-reversed);
        --color: var(--lr-color-tertiary-reversed);
        &:not([disabled]):hover {
            --background-color: var(--lr-background-tertiary-reversed-hover);
            --color: var(--lr-color-tertiary-reversed-hover);
        }
        &:not([disabled]):active {
            --background-color: var(--lr-background-tertiary-reversed-active);
            --color: var(--lr-color-tertiary-reversed-active);
        }
    }

    :host([variant='tertiary-transparent']) .button {
        --background-color: transparent;
    }
    :host([variant='tertiary-transparent'][reversed]) .button {
        --background-color: transparent;
    }

    :host([variant='link']) .button {
        background-color: transparent;
        --color: var(--lr-color-link);
        padding: 0;
        &[disabled] {
            --color: var(--lr-color-link-disabled);
        }
        &:not([disabled]):hover {
            --color: var(--lr-color-link-hover);
        }
        &:not([disabled]):active {
            --color: var(--lr-color-link-active);
        }
    }

    :host([variant='link'][reversed]) .button {
        background-color: transparent;
        --color: var(--lr-color-link-reversed);
        &[disabled] {
            --color: var(--lr-color-link-reversed-disabled);
        }
        &:not([disabled]):hover {
            --color: var(--lr-color-link-reversed-hover);
        }
        &:not([disabled]):active {
            --color: var(--lr-color-link-reversed-active);
        }
        &:not([disabled]):focus-visible {
            outline-offset: 0px;
        }
    }

    :host([icon]) .button .icon,
    :host([icon]) .button .loading-spinner {
        margin-top: -64px;
        margin-bottom: -64px;
    }

    :host([variant='link']) ::slotted() {
        margin: 0;
        transform: scale(0.75);
        width: 2rem;
    }

    :host([fluid]) .button {
        width: 100%;
    }

    :host([fluid='only-mobile']) .button {
        width: auto;
        @media (max-width: ${unsafeCSS(theme.devices.mobile.max)}px) {
            width: 100%;
        }
    }
`;
